// helpdesk-frontend/src/components/Header.js

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../hooks/useAuth";
import { czyPokazacPanelAdministracyjny } from "../constants/adminTilesConfig";
import { useUI } from "../hooks/useUI";
import styles from "./Header.module.css";
import apiService from "../api/apiService";
import {
    FiMoon,
    FiSun,
    FiMenu,
    FiLogOut,
    FiSettings,
    FiSearch,
    FiUser,
    FiChevronDown,
    FiMessageSquare,
    FiArrowLeft,
    FiCommand,
    FiEye,
    FiType,
    FiBookOpen,
    FiMinus,
    FiPlus,
    FiRotateCcw,
    FiDroplet,
    FiKey,
    FiMonitor,
} from "react-icons/fi";
import ChangelogModal from "./ChangelogModal/ChangelogModal";
import { useTheme } from "../context/ThemeContext";
import NotificationBell from "./Notifications/NotificationBell";
import { localizedPath } from "../hooks/useLocalizedNavigate";
import { VIEW_MODULE_QUEUE_ROUTES } from "../constants/routes";
import { adresModuluObslugi, widokKolejkiZAdresu } from "../utils/celModuluObslugi";
// Osobna linia importu z tego samego pliku ŚWIADOMIE: zapadka pages/__tests__/powrotWNaglowkuKolejki.0930 (3) pilnuje
// dokładnego kształtu linii wyżej (jedna reguła adresu modułu) — dopisanie trzeciej nazwy do niej zaczerwieniłoby ją.
import { celPowrotuZAdresu, oslonyPowrotu } from "../utils/celModuluObslugi";
import { pathStartsWith, translateCurrentPath } from "../utils/routeHelpers";
import { setUserLang, USER_LANG_STORAGE_KEY } from "../utils/language";
import { useAccessibility } from "../context/AccessibilityContext";
import { usePersonalization } from "../context/PersonalizationContext";
import FlagIcon from "./common/FlagIcon/FlagIcon";
import UserAvatar from "./common/UserAvatar/UserAvatar";
import { pobierzZPamieci, tozsamoscKonta, KLUCZE_POWLOKI } from "../hooks/usePamiecPowloki";
import { pobierzMojeModulyObslugi } from "../hooks/useModulyObslugi";
import ZmianaHaslaOkno from "./ZmianaHasla/ZmianaHaslaOkno";
import { ZDARZENIE_OTWORZ_OKNO_WYGLADU, ZDARZENIE_OTWORZ_USTAWIENIA_WYGLADU } from "./OknoWygladu/OknoWygladu";

/* [polecenie właściciela 2026-10-03] Motyw w liście „Dostępność i personalizacja” — to miejsce wskazuje okno
   „Wygląd aplikacji” („Ikona oka → Dostępność i personalizacja → Motyw”). Księżyc obok zostaje skrótem. */
const MOTYWY_W_MENU = [
    { id: "light", etykieta: "Jasny", Ikona: FiSun },
    { id: "dark", etykieta: "Ciemny", Ikona: FiMoon },
];

const Header = () => {
    const { user, logout, hasPermission, isModuleGloballyHidden, isModuleRestricted } = useAuth();
    const { toggleMobileSidebar, openWyszukiwarke, isMobile } = useUI();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // [KA-14 2026-09-26] „Zmień hasło” tylko dla konta logującego się hasłem aplikacji (pytamy serwer raz, przy
    // pierwszym otwarciu menu). [KA-05] „Wyloguj ze wszystkich urządzeń” — dla każdego.
    const [maHaslo, setMaHaslo] = useState(false);
    const [oknoHasla, setOknoHasla] = useState(false);
    const [komunikatHasla, setKomunikatHasla] = useState(null);
    const statusHaslaPobrany = useRef(false);
    const [isChangelogOpen, setIsChangelogOpen] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);
    const [languages, setLanguages] = useState([]);
    // Start z cache'u przeglądarki (żeby nie mignęło w złym języku), a zaraz potem
    // synchronizacja z preferencją zapisaną NA KONCIE — patrz efekt poniżej.
    const [selectedLang, setSelectedLang] = useState(() => {
        return localStorage.getItem(USER_LANG_STORAGE_KEY) || "pl";
    });

    const [changelog, setChangelog] = useState([]);
    const [changelogLoading, setChangelogLoading] = useState(true);
    // latestVersion usunięty — zastąpiony przez NotificationBell
    const [langOverlay, setLangOverlay] = useState(null);
    // [G2 2026-08-31] Komunikat, gdy usługa tłumaczeń nie odpowiada. Milczenie w tym miejscu było
    // najgorszą z opcji: człowiek klikał język, nic się nie zmieniało i nie wiedział dlaczego.
    const [langBlad, setLangBlad] = useState(null);
    const [isA11yOpen, setIsA11yOpen] = useState(false);

    const { theme, toggleTheme, ustawMotyw } = useTheme();
    // Atrapy ThemeContext w zapadkach nagłówka oddają samo { theme, toggleTheme } — wtedy wybór idzie przełącznikiem.
    const wybierzMotyw = (id) => {
        if (typeof ustawMotyw === "function") ustawMotyw(id);
        else if (id !== theme) toggleTheme();
    };

    useEffect(() => {
        if (!isMenuOpen || statusHaslaPobrany.current || typeof apiService.statusMojegoHasla !== "function") return;
        statusHaslaPobrany.current = true;
        let aktywny = true;
        Promise.resolve(apiService.statusMojegoHasla())
            // [HASLA-LOKALNE 2026-09-27] „pozwalaj im na zmiane i dla admina ale tylko dla lokalsow” — pozycja dla konta
            // LOKALNEGO (serwer: zmianaDozwolona). BYŁO: maHaslo, czyli także konto Office 365 z hashem hasła.
            .then((res) => { if (aktywny) setMaHaslo(res?.data?.zmianaDozwolona === true); })
            .catch(() => { /* bez statusu pozycja po prostu się nie pokazuje */ });
        return () => { aktywny = false; };
    }, [isMenuOpen]);
    const {
        fontScaleLabel,
        highContrast,
        readerMode,
        canIncrease,
        canDecrease,
        isDefault,
        increaseFontScale,
        decreaseFontScale,
        toggleHighContrast,
        toggleReaderMode,
        resetAll,
    } = useAccessibility();
    const {
        accent, font, accents, fonts, setAccent, setFont,
        resetPersonalization, isDefault: persoIsDefault,
    } = usePersonalization();

    const menuRef = useRef(null);
    const langRef = useRef(null);
    const a11yRef = useRef(null);

    // [brief 2026-09-01, pkt L10] Panel administracyjny — także dla osoby BEZ rangi administratora,
    // o ile ma klucz choćby jednego kafelka. Spójnie z trasą /admin i menu bocznym: przycisk ma się
    // POJAWIĆ po nadaniu uprawnienia, a nie dopiero po podniesieniu rangi konta do administratora.
    // [T2b 2026-09-14] Ten sam predykat co kafelki /admin (`czyKafelekDostepny`: bramka trasy + widoczność modułu).
    const canAccessAdminPanel = user?.type === 3
        || czyPokazacPanelAdministracyjny({ hasPermission, isModuleRestricted, isModuleGloballyHidden, user });
    const canAccessTickets = hasPermission("TicketView");
    // [KONFIGURATOR 2026-07-28] „Obsługa HR" jest modułem WBUDOWANYM zarządzanym z konfiguratora
    // (/admin/categories → wiersz hrManagement, ModuleVisibilities): wyłączenie modułu ukrywa
    // przycisk, a nie tylko blokuje trasę (wcześniej user klikał i dostawał ekran „Moduł wyłączony").
    const canAccessHrManagement = hasPermission("HrAccess")
        && !isModuleGloballyHidden("hrManagement")
        && !isModuleRestricted("hrManagement");

    /* [polecenie właściciela 2026-09-21] „jeżeli centrum aktywności jest wyłączone, to też znika dzwonek
       z header". Dzwonek był jedynym wejściem do centrum powiadomień, które NIE pytało o widoczność
       modułu: pozycja menu znikała, widżet pulpitu znikał, a dzwonek dalej otwierał panel i prowadził
       na trasę, którą ProtectedRoute oddaje jako „Moduł ukryty". Ta sama para predykatów, co wszędzie
       indziej (globalnie dla firmy + deny-lista konta). */
    const czyCentrumPowiadomienWidoczne = !isModuleGloballyHidden("notificationCenter")
        && !isModuleRestricted("notificationCenter");

    // [G2] Dynamiczne przyciski „Obsługa …" z modułów zgłoszeń (TicketModules), do których
    // user należy (agent desku / dostęp do skrzynki / moderator+admin). Etykieta = ButtonLabel
    // z konfiguratora. Moduł 'itsm' prowadzi na legacy landing /obsluga-it.
    const [myTicketModules, setMyTicketModules] = useState([]);
    /* [WYDAJNOŚĆ 2026-09-12] Skład modułów obsługi zmienia się w /admin/categories, a nie w trakcie
       klikania po menu — pytamy o niego RAZ na sesję karty. Wpis jest wiązany z identyfikatorem
       konta (inne konto = inna odpowiedź) i kasowany przy zmianie uprawnień (`uprawnienia-zmienione`),
       bo to właśnie nadanie/odebranie TicketView zmienia tę listę. Zmierzone: /api/ticket-modules/my
       243 wywołania w 3 dni. */
    /* ⚠ Tożsamość liczy WSPÓLNA funkcja `tozsamoscKonta`, a pobranie idzie przez WSPÓLNĄ
       `pobierzMojeModulyObslugi` — nagłówek i trzy strony (kolejka zgłoszeń, landing IT, landing HR)
       muszą trafiać w TEN SAM wpis. Przepisane tu z ręki wyrażenie („user?.id ?? …") albo własny
       `pobieracz` to pudło w pamięci i ciche DRUGIE zapytanie — bez żadnego błędu na ekranie. */
    const idKontaNaglowek = tozsamoscKonta(user);
    useEffect(() => {
        if (!canAccessTickets) { setMyTicketModules([]); return undefined; }
        let zywy = true;
        pobierzMojeModulyObslugi({ tozsamosc: idKontaNaglowek })
            .then((lista) => { if (zywy) setMyTicketModules(Array.isArray(lista) ? lista : []); })
            .catch(() => { if (zywy) setMyTicketModules([]); });
        return () => { zywy = false; };
    }, [canAccessTickets, idKontaNaglowek]);
    // [MODUŁY DYNAMICZNE 2026-07-31] /obsluga-hr (i /hr-management — lustro routes.js:hrManagement)
    // podświetla przycisk HR; KAŻDY inny adres /obsluga-<kod> to landing modułu ticketowego i zapala
    // przyciski „Obsługa …". Granica segmentu celowo („/obsluga-hr" ≠ „/obsluga-hr2"), żeby moduł
    // o kodzie zaczynającym się od „hr…" nie zapalał przycisku HR.
    const startsWithSegment = (pathname, base) => pathname === base || pathname.startsWith(base + "/");
    const isOnHrManagement = startsWithSegment(location.pathname, "/obsluga-hr")
        || startsWithSegment(location.pathname, "/hr-management");
    const isOnTickets = pathStartsWith(location.pathname, "tickets")
        || pathStartsWith(location.pathname, "itManagement")
        || (location.pathname.startsWith("/obsluga-") && !isOnHrManagement);
    // Moduł dynamiczny prowadzący na stronę wbudowaną /obsluga-hr zastępuje statyczny przycisk HR
    // (koniec duplikatu „Obsługa HR" x2). Statyczny zostaje jako fallback dla kadrowców z HrAccess,
    // którzy NIE są członkami modułu zgłoszeń HR — te populacje się nie pokrywają.
    const hasDynamicHrButton = myTicketModules.some((m) => m.viewModuleCode === "hrManagement");


    // [P4 2026-09-01] CEL PRZYCISKU MODUŁU — brak klucza STRONY WBUDOWANEJ nie kasuje wejścia,
    // tylko przestawia je na kolejkę zgłoszeń tego modułu.
    // BYŁO ŹLE: `.filter((m) => !m.gateKey || hasPermission(m.gateKey))` wyrzucał moduł w całości.
    // Obsada desku „Obsługa HR" konfiguruje się w /admin/categories NIEZALEŻNIE od nadania
    // HrAccess, więc realnie istnieje agent z TicketView, który obsługuje zgłoszenia kadrowe, a
    // klucza do strony /obsluga-hr nie ma. Ta osoba nie dostawała nawet mylącego przycisku, tylko
    // ZERO: jedyne pozostałe wejście do kolejki (przycisk „Zgłoszenia" na /obsluga-hr) stoi za tym
    // samym kluczem, którego jej brakuje. To ta sama klasa błędu, którą R1 opisuje jako naprawioną.
    // TERAZ: cel przycisku = kolejka modułu (VIEW_MODULE_QUEUE_ROUTES → /obsluga-hr/zgloszenia,
    // brama TicketView jak na starym adresie). Lekcja N1 zostaje w mocy — przycisk prowadzi tam,
    // gdzie ta osoba realnie wchodzi. Moduł widokowy BEZ trasy kolejki nadal znika (nie ma dokąd).
    const celPrzyciskuModulu = useCallback((m) => {
        /* [polecenie właściciela 2026-09-30, B1] Moduł bez strony wbudowanej — adres z JEDNEJ reguły
           (`utils/celModuluObslugi`), tej samej, którą przycisk powrotu w nagłówku kolejki zgłoszeń
           wraca na landing modułu. Wcześniej kolejka liczyła ten adres po swojemu (`/obsluga-it/<kod>`). */
        if (!m.viewModuleCode)
            return adresModuluObslugi(m.code);
        if (!m.gateKey || hasPermission(m.gateKey)) return localizedPath(m.viewModuleCode);
        const trasaKolejki = VIEW_MODULE_QUEUE_ROUTES[m.viewModuleCode];
        return trasaKolejki ? localizedPath(trasaKolejki) : null;
    }, [hasPermission]);

    /**
     * [polecenie właściciela 2026-09-06] JEDEN KAFELEK „Obsługa", a wybór modułu dopiero pod nim.
     *
     * BYŁO ŻLE: każdy moduł obsługi dokładał WŁASNY zielony przycisk do górnego paska. Przy dwóch
     * modułach pasek niosł „Obsługa IT" i „Obsługa HR" obok siebie, a każdy kolejny moduł
     * (konfigurowany w /admin/categories) zabierałby kolejne miejsce — pasek rósłby bez końca.
     *
     * TERAZ liczba modułów decyduje o kształcie, a nie o liczbie przycisków:
     *   • zero  — nie ma czego pokazywać (i to jest cała reguła dostępu: brak nadania = brak wejścia),
     *   • jeden — jeden przycisk prowadzący wprost do tego modułu, bez zbędnego kroku,
     *   • więcej — jeden przycisk „Obsługa" i lista do wyboru.
     *
     * ⚠ Lista bierze się WYŁĄCZNIE z modułów, które ta osoba realnie obsługuje. Przycisk nigdy nie
     * prowadzi na ekran „Brak dostępu" — `celPrzyciskuModulu` zwraca `null`, gdy nie ma dokąd wejść.
     */
    // [poprawka właściciela 2026-10-01] Strona kolejki zgłoszeń? (IT → "itManagement", HR → "hrManagement", inne → null)
    const widokKolejki = widokKolejkiZAdresu(location.pathname);
    const moduleObslugi = useMemo(() => {
        const lista = [];
        /* [zgłoszenie właściciela 2026-09-16] „wyłącz mi przyciski obsługi HR/ITSM (…) NIE MA ICH
           W HEADER!!!" — telefon służy do podstawowej pracy użytkownika, a nie do prowadzenia kolejki.
           Odcinamy je TU, w jednym miejscu, a nie przy każdym z dwóch kształtów przycisku: inaczej
           kolejny kształt (albo kolejny moduł) znów pojawiłby się na telefonie.
           ⚠️ To zdejmuje WEJŚCIE z nagłówka, a nie same strony /obsluga-*. Ten sam brief każe oknu
           zgłoszenia przeżyć odświeżenie „głównie w telefonie" — kto dostanie link do zgłoszenia,
           ma je otworzyć. Zabieramy skrót, nie odbieramy dostępu. */
        if (isMobile) return lista;
        if (canAccessTickets) {
            myTicketModules
                .filter((m) => !m.viewModuleCode
                    || (!isModuleGloballyHidden(m.viewModuleCode) && !isModuleRestricted(m.viewModuleCode)))
                .forEach((m) => {
                    const cel = celPrzyciskuModulu(m);
                    if (!cel) return;
                    lista.push({
                        klucz: m.code,
                        etykieta: m.buttonLabel || m.name,
                        cel,
                        kolor: m.color || null,
                        /* [poprawka właściciela 2026-10-01] Strona wbudowana, której kolejką jest /…/zgloszenia — po niej
                           nagłówek rozpoznaje „moduł tej kolejki” (`widokKolejkiZAdresu`). Moduł IT bez strony wbudowanej
                           (kod itsm / archiwalny tickets) to landing /obsluga-it. */
                        widok: m.viewModuleCode
                            || (["itsm", "tickets"].includes(String(m.code || "").toLowerCase()) ? "itManagement" : null),
                        // Strona wbudowana rozstrzyga o podświetleniu: hrManagement → /obsluga-hr,
                        // itManagement → /obsluga-it (moduł IT dostał swój wpis 2026-09-06).
                        aktywny: m.viewModuleCode === "hrManagement" ? isOnHrManagement
                            : (!m.viewModuleCode || m.viewModuleCode === "itManagement") ? isOnTickets
                            : false,
                    });
                });
        }
        // Moduł kadrowy bez własnego wpisu w TicketModules — wchodzi tak samo, przez nadanie HrAccess.
        if (canAccessHrManagement && !hasDynamicHrButton) {
            lista.push({
                klucz: "hrManagement",
                etykieta: "Obsługa HR",
                cel: localizedPath("hrManagement"),
                kolor: null,
                aktywny: isOnHrManagement,
                widok: "hrManagement",
            });
        }
        /* [poprawka właściciela 2026-10-01] Kolejka IT bez modułu IT na liście (klucz TicketView bez członkostwa w module —
           trasa kolejki wpuszcza samym kluczem): przycisk powrotu stał dotąd w kolejce, więc nagłówek nie może go zgubić.
           Wpis powstaje WYŁĄCZNIE na stronie kolejki IT i tylko, gdy landing /obsluga-it jest dla tej osoby otwarty
           (ta sama para predykatów widoczności co ProtectedRoute) — nigdy obok istniejącego wpisu IT. */
        if (widokKolejki === "itManagement" && canAccessTickets && !lista.some((m) => m.widok === "itManagement")
            && !isModuleGloballyHidden("itManagement") && !isModuleRestricted("itManagement")) {
            lista.push({
                klucz: "itManagement",
                etykieta: "Obsługa IT",
                cel: localizedPath("itManagement"),
                kolor: null,
                aktywny: true,
                widok: "itManagement",
            });
        }
        return lista;
    }, [isMobile, canAccessTickets, canAccessHrManagement, hasDynamicHrButton, myTicketModules,
        isModuleGloballyHidden, isModuleRestricted, celPrzyciskuModulu, isOnHrManagement, isOnTickets, widokKolejki]);

    /* [poprawka właściciela 2026-10-01] „Przycisk «Obsługa IT» ma być w HEADER, a nie w tej belce”. Drogą powrotu z kolejki
       zgłoszeń (IT: /obsluga-it/zgloszenia, HR: /obsluga-hr/zgloszenia) jest ZWYKŁY przycisk obsługi w nagłówku:
         • jeden moduł — „Obsługa IT” prowadzi na landing modułu;
         • kilka modułów — „Obsługa N” z listą modułów (moduł tej kolejki oznaczony jako bieżący).
       [poprawka właściciela 2026-10-01, po wdrożeniu du] „Na /obsluga-it/zgloszenia mój przycisk zmienia styl, a nie powinien” —
       przycisk dzielony z ramką w kolorze modułu zdjęty. Na stronie kolejki przycisk wygląda DOKŁADNIE jak na pulpicie:
       te same klasy, ten sam napis, bez przygaszenia „jesteś tutaj” (z kolejki to droga powrotu, nie bieżąca strona).
       Zapadka: obslugaWNaglowkuNaKolejce.1001 porównuje znacznik przycisku na kolejce i na pulpicie.
       Telefon: bez przycisków obsługi (polecenie 16.09 — `isMobile` wyżej). */
    const modulKolejki = widokKolejki ? (moduleObslugi.find((m) => m.widok === widokKolejki) || null) : null;

    const [obslugaOtwarta, setObslugaOtwarta] = useState(false);
    const obslugaRef = useRef(null);
    const obslugaListaRef = useRef(null);

    // [polecenie właściciela 2026-09-09] Lista modułów renderowana PORTALEM do <body>, pozycjonowana względem przycisku.
    // BYŁO ŹLE: lista siedziała w `.ctaGroup`, który ma `overflow-x:auto; overflow-y:hidden` (żeby przyciski „Obsługa…”
    // nie wypychały menu profilu poza ekran) — pozycjonowana absolutnie lista wychodziła POD grupę i była przycinana:
    // w DOM istniała (aria-expanded="true", dwie pozycje), ale osoba z dwoma modułami nie widziała nic i nie miała jak
    // wybrać, do której obsługi wejść. Portal wyprowadza listę spod overflow, a pozycja liczy się z prostokąta przycisku.
    const [pozycjaListy, setPozycjaListy] = useState(null);
    useLayoutEffect(() => {
        if (!obslugaOtwarta) { setPozycjaListy(null); return undefined; }
        const ustaw = () => {
            const r = obslugaRef.current?.getBoundingClientRect?.();
            if (r) setPozycjaListy({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
        };
        ustaw();
        window.addEventListener("resize", ustaw);
        window.addEventListener("scroll", ustaw, true);
        return () => {
            window.removeEventListener("resize", ustaw);
            window.removeEventListener("scroll", ustaw, true);
        };
    }, [obslugaOtwarta]);

    // Klik poza przyciskiem I poza listą zamyka ją — lista jest w portalu, więc sam `obslugaRef` już nie wystarcza
    // (mousedown w pozycji listy zamykałby ją, zanim doszłoby do kliknięcia i nawigacji).
    useEffect(() => {
        if (!obslugaOtwarta) return undefined;
        const pozaLista = (e) => {
            const wPrzycisku = obslugaRef.current && obslugaRef.current.contains(e.target);
            const wLiscie = obslugaListaRef.current && obslugaListaRef.current.contains(e.target);
            if (!wPrzycisku && !wLiscie) setObslugaOtwarta(false);
        };
        document.addEventListener("mousedown", pozaLista);
        return () => document.removeEventListener("mousedown", pozaLista);
    }, [obslugaOtwarta]);

    /* ── Przycisk „Powrót” ──────────────────────────────────────────────────────────────────────────
       [polecenie właściciela 2026-10-02, EB-1] „w /obsluga-it/zgloszenia w header, tak samo jak w admin, ale też dla pozostałych
       modułów, dodaj przycisk Powrót z odpowiednią nawigacją powrotu do modułu”.
       BYŁO ŹLE: przycisk widział tylko administrator (ranga 3) na /admin/*, /e-teczka, /itam/*, a prowadził przez `navigate(-1)`,
       czyli historię przeglądarki — po F5, z wklejonego adresu albo z linku w mailu historia jest pusta lub wyprowadza poza
       aplikację. Delegat z kluczem kafelka (bez rangi) nie miał przycisku wcale.
       TERAZ cel liczy JEDNA czysta reguła z adresu (`celPowrotuZAdresu`, utils/celModuluObslugi.js) — „poziom wyżej”, ten sam po
       odświeżeniu i po zmianie języka (liczone z bieżącego pathname, bez memoizacji). `location.state.from` z landingu obsługi
       nadal wygrywa. `null` = strona bez rodzica (pulpit, landingi) = brak przycisku. Zero `navigate(-1)`.
       ⚠ Przycisk „Obsługa” (niżej) jest OSOBNYM elementem i nie zmienia ani klasy, ani atrybutu — zapadka obslugaWNaglowkuNaKolejce.1001 (0)
       porównuje jego znacznik na kolejce i na pulpicie. */
    // [przegląd EB-1 2026-10-03] Osłona kluczy: landing IT za TicketView, landing HR za HrAccess — bez klucza cel to pulpit,
    // żeby przycisk nie prowadził na ekran „Brak dostępu” (księgowa na /obsluga-hr/rozliczenia, compliance na /obsluga-hr/sygnalista).
    // [domknięcie eb 2026-10-04] Wszystkie osłony z JEDNEGO miejsca (oslonyPowrotu): także widoczność modułu IT, klucz przeglądu ITAM,
    // kafelki procedur i analityki — oraz `search` (karta i rejestracja decyzji czytają `?doc` tak jak ich strony).
    const celPowrotu = celPowrotuZAdresu(location.pathname, {
        stateFrom: location.state?.from,
        search: location.search,
        ...oslonyPowrotu({ user, hasPermission, isModuleGloballyHidden, isModuleRestricted }),
    });
    const showBackButton = !!celPowrotu;

    // Load languages from API
    /* [WYDAJNOŚĆ 2026-09-12] Lista języków to SŁOWNIK GLOBALNY (ten sam dla wszystkich kont),
       zmieniany wyłącznie w /admin/languages. Pytamy o nią raz na sesję karty — zmierzone
       /api/languages 170 wywołań w 3 dni. Odpowiedź nie zależy od uprawnień, więc pamięć nie
       jest wiązana z kontem; siatką bezpieczeństwa zostaje TTL z `usePamiecPowloki`. */
    useEffect(() => {
        let zywy = true;
        pobierzZPamieci(KLUCZE_POWLOKI.JEZYKI, {
            pobieracz: () => apiService.getTranslationLanguages().then((res) => res?.data || []),
            tozsamosc: null,
        })
            .then((lista) => {
                if (zywy) setLanguages(lista || []);
            })
            .catch(() => {
                if (!zywy) return;
                // Fallback: domyślne języki. Bez pola z emoji chorągiewki — flagę
                // rysuje FlagIcon po kodzie GT; emoji flag nie ma w foncie systemowym
                // Windows, więc Chrome pokazywał zamiast nich gołe litery (poz. 23).
                setLanguages([
                    { id: 1, gtCode: "pl", displayCode: "PL", nativeName: "Polski", sortOrder: 1 },
                    { id: 2, gtCode: "en", displayCode: "EN", nativeName: "English", sortOrder: 2 },
                ]);
            });
        return () => { zywy = false; };
    }, []);

    useEffect(() => {
        fetch("/changelog.json")
            .then((response) => response.json())
            .then((data) => {
                if (data.changeLog && data.changeLog.length > 0) {
                    const sortedLog = [...data.changeLog].sort((a, b) =>
                        b.version.localeCompare(a.version)
                    );
                    setChangelog(sortedLog);
                }
                setChangelogLoading(false);
            })
            .catch(() => {
                setChangelogLoading(false);
            });
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setIsMenuOpen(false);
            if (langRef.current && !langRef.current.contains(e.target)) setIsLangOpen(false);
            if (a11yRef.current && !a11yRef.current.contains(e.target)) setIsA11yOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // [2026-10-03] „Przejdź do ustawień wyglądu” z okna „Wygląd aplikacji” rozwija tę listę.
    // [poprawka po przeglądzie 2026-10-04] …i przenosi fokus na zaznaczony motyw — osoba z klawiaturą nie zostaje
    // na <body> po zamknięciu okna. Po zamknięciu okno oddaje fokus temu, co było wcześniej, więc ustawiamy go
    // dopiero w następnym kroku (setTimeout 0), już po tym oddaniu.
    const fokusNaMotyw = useRef(false);
    const grupaMotywuRef = useRef(null);
    useEffect(() => {
        const otworz = () => { fokusNaMotyw.current = true; setIsMenuOpen(false); setIsA11yOpen(true); };
        window.addEventListener(ZDARZENIE_OTWORZ_USTAWIENIA_WYGLADU, otworz);
        return () => window.removeEventListener(ZDARZENIE_OTWORZ_USTAWIENIA_WYGLADU, otworz);
    }, []);
    useEffect(() => {
        if (!isA11yOpen || !fokusNaMotyw.current) return undefined;
        fokusNaMotyw.current = false;
        const t = setTimeout(() => {
            const grupa = grupaMotywuRef.current;
            const cel = grupa && (grupa.querySelector('[aria-pressed="true"]') || grupa.querySelector("button"));
            if (cel && typeof cel.focus === "function") cel.focus();
        }, 0);
        return () => clearTimeout(t);
    }, [isA11yOpen]);

    const userInitial = user?.firstName ? user.firstName.charAt(0).toUpperCase() : "?";

    const getRoleName = (type) => {
        switch (type) {
            case 3: return "Administrator";
            // [O-9, 2026-09-08] Ranga moderatora usunięta. Wartość zastana nie ma udawać
            // „Pracownik" — to jest różnica między „nie wiem" a „wiem, że to nie tak".
            case 2: return "Ranga zniesiona";
            default: return "Pracownik";
        }
    };

    /**
     * Zmiana języka BEZ przeładowania strony.
     *
     * Wcześniej: ustawienie cookie → twardy `window.location.href` z hashem `_lang=<timestamp>`.
     * Reload był konieczny, bo skrypt Google Translate ładował się warunkowo tylko przy starcie
     * w języku innym niż polski — startując po polsku nie było czym przełączyć. Teraz skrypt
     * dociągany jest leniwie (`ensureGoogleTranslateLoaded` w index.html), więc tłumaczymy żywy DOM.
     *
     * Wybór zapisujemy NA KONCIE użytkownika, żeby aplikacja wstawała w jego języku
     * także na innym urządzeniu. localStorage zostaje wyłącznie jako cache na czas startu.
     */
    const handleLangSelect = useCallback(async (gtCode) => {
        if (langOverlay) return; // blokada wielokrotnych kliknięć

        const targetLang = languages.find((l) => l.gtCode === gtCode);
        const poprzedniJezyk = selectedLang;

        setLangBlad(null);
        setSelectedLang(gtCode);
        setIsLangOpen(false);
        // [A15-02] Zapis przez `setUserLang` — dokłada sygnał, po którym App przelicza tablicę
        // tras. Gołe `localStorage.setItem` zostawiało trasy w starym języku i `navigate(...)`
        // dwie linie niżej trafiał pod adres, którego router nie znał → pętla przekierowań.
        setUserLang(gtCode);
        setLangOverlay(targetLang?.nativeName || gtCode.toUpperCase());

        // Trasy są dwujęzyczne (PL/EN) — adres dopasowujemy przez router, bez przeładowania.
        const newRouteLang = gtCode === "pl" ? "pl" : "en";
        const translatedPath = translateCurrentPath(location.pathname, newRouteLang);
        if (translatedPath && translatedPath !== location.pathname) {
            navigate(translatedPath + location.search, { replace: true });
        }

        // [polecenie właściciela 2026-09-09] Preferencję zapisujemy PRZED przełączeniem: most Google
        // (public/index.html) domyka zmianę ZWYKŁYM przeładOWANIEM strony, gdy widżet nie przełączy się w miejscu
        // (zmierzone: powrót na polski) — zapis wysłany dopiero po fakcie zostałby urwany i konto pamiętałoby
        // stary język, więc po zalogowaniu na innym urządzeniu interfejs wstawałby nie w tym języku.
        // Gdy przełączenie się nie uda, preferencja wraca do poprzedniej (gałąź awarii niżej).
        try { await apiService.setMyLanguage(gtCode); }
        catch (err) { console.error("[Header] Nie zapisano języka na koncie:", err); }

        let switched = false;
        try {
            switched = window.changeGoogleTranslateLanguage
                ? await window.changeGoogleTranslateLanguage(gtCode)
                : false;
        } catch (err) {
            console.error("[Header] Zmiana języka nie powiodła się:", err);
        }

        // Preferencję zapisujemy TYLKO gdy język faktycznie się przełączył — inaczej konto
        // pamiętałoby język, którego interfejs nie pokazuje (rozjazd stanu po ponownym wejściu).
        if (switched) {
            // Preferencja zapisana wyżej, przed przełączeniem.
            setLangOverlay(null);
            return;
        }

        // ── Powrót na polski ──
        // Strona nietknięta tłumaczem = nie ma czego przełączać. To jest sukces, nie awaria.
        if (gtCode === "pl") {
            // Preferencja zapisana wyżej, przed przełączeniem.
            setLangOverlay(null);
            return;
        }

        // ── Tłumaczenie NIE zadziałało ──
        // [G2 2026-08-31] BYŁO ŹLE: tutaj stało „awaryjne twarde przeładowanie":
        //   window.location.href = targetUrl + "#_lang=" + Date.now();
        // Dwie linie wyżej kod już zrobił navigate(translatedPath), więc przeglądarka STAŁA pod tym
        // samym adresem, a przypisanie URL-a różniącego się WYŁĄCZNIE fragmentem (#…) nie przetadowuje
        // strony — to tylko skok do kotwicy. Przeładowanie nigdy się nie odbywało, język się nie
        // zmieniał, i stąd wzięło się „język wymaga Ctrl+F5" ze zgłoszenia właściciela.
        //
        // Przeładowanie i tak by nie pomogło: `switched === false` znaczy, że skrypt Google Translate
        // NIE WSTAŁ (odcięty translate.google.com albo upłynął limit 10 s) — po odswieżeniu ten sam
        // skrypt byłby pobierany z tego samego, niedostępnego adresu. Całe tłumaczenie aplikacji opiera
        // się na tej usłudze; własnych tłumaczeń nie ma. Dlatego zamiast udawać, że coś się dzieje,
        // MÓWIMY o tym wprost i wracamy do poprzedniego języka, żeby stan interfejsu się nie rozjechał.
        setSelectedLang(poprzedniJezyk);
        setUserLang(poprzedniJezyk);
        apiService.setMyLanguage(poprzedniJezyk).catch((err) =>
            console.error("[Header] Nie przywrócono języka na koncie:", err));
        if (translatedPath && translatedPath !== location.pathname) {
            const powrot = translateCurrentPath(location.pathname, poprzedniJezyk === "pl" ? "pl" : "en");
            if (powrot) navigate(powrot + location.search, { replace: true });
        }
        setLangOverlay(null);
        setLangBlad(
            "Nie udało się przełączyć języka: usługa tłumaczeń Google (translate.google.com) nie odpowiada. "
            + "Najczęściej jest zablokowana w sieci firmowej albo przez dodatek w przeglądarce — to ustawienie "
            + "poza aplikacją. Interfejs zostaje w dotychczasowym języku."
        );
    }, [languages, location.pathname, location.search, langOverlay, navigate, selectedLang]);

    /**
     * Język z KONTA jest źródłem prawdy. Po zalogowaniu (także na nowym urządzeniu, gdzie
     * localStorage jest pusty) dociągamy preferencję i — jeśli różni się od aktualnie
     * pokazywanego — przełączamy interfejs w miejscu, bez przeładowania.
     */
    useEffect(() => {
        const accountLang = user?.preferredLanguage;
        if (!accountLang || accountLang === selectedLang) return;

        setSelectedLang(accountLang);
        // [A15-02] Ta ścieżka (język z KONTA, tuż po zalogowaniu) nie nawiguje i nie przeładowuje
        // strony — bez sygnału trasy zostawały polskie, a pierwsze kliknięcie w menu wpadało
        // w pętlę LocalizedRedirect ↔ HomeRedirect.
        setUserLang(accountLang);

        if (window.changeGoogleTranslateLanguage) {
            Promise.resolve(window.changeGoogleTranslateLanguage(accountLang))
                .catch((err) => console.error("[Header] Nie udało się zastosować języka konta:", err));
        }
        // Celowo bez `selectedLang` w zależnościach — reagujemy na zmianę KONTA (logowanie),
        // a nie na ręczne przełączenie języka przez użytkownika.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.preferredLanguage]);

    /**
     * ⌘K / Ctrl+K → szybki dostęp.
     *
     * [poz. 24, brief 4.09] Pole „Szukaj" od zawsze RYSOWAŁO odznakę „⌘K", ale nikt tego
     * skrótu nie nasłuchiwał — obietnica bez pokrycia. Przy okazji usuwania kratki
     * „Szybki dostęp" z paska skrót staje się realnym, drugim wejściem do tego samego okna.
     *
     * `defaultPrevented` to ustępstwo dla stron, które mają WŁASNE Ctrl+K (panel
     * administracyjny ustawia tam ostrość na swojej wyszukiwarce i nasłuchuje w fazie
     * przechwytywania, więc zdąży zgłosić „to moje" przed tym miejscem).
     */
    /* [S6, polecenie właściciela 2026-09-13] Pole „Szukaj" i Ctrl+K otwierają WYSZUKIWARKĘ (treści + kafelki
       w granicach uprawnień, historia, wyniki w szufladzie). Poz. 24 z briefu 4.09 (skrót Ctrl+K ma realne
       działanie) zostaje w mocy.

       [zgłoszenie właściciela 2026-09-16] „Usuń mi CAŁY widget dla wyszukiwarki dla urządzeń mobilnych."
       BYŁO PÓŁ KROKU: 13.09 wyszukiwarka zniknęła z telefonu, ale pole zostało — lupa otwierała „Szybki
       dostęp". Dla człowieka z telefonem to dalej było pole „Szukaj…" zajmujące pół paska i obiecujące
       szukanie, które tu nie działa. TERAZ pola nie ma wcale, a skrót klawiszowy jest bezczynny (telefon
       z klawiaturą też nie otworzy okna, którego nie ma).

       ⚠️ KONSEKWENCJA, ŻEBY NIE ZGINĘŁA: lupa była JEDYNYM wejściem do okna „Szybki dostęp", więc na
       telefonie tego okna już nie ma. Nic przez to nie znika z aplikacji — „Szybki dostęp" był listą
       skrótów do stron, które na telefonie i tak stoją w menu bocznym (drawer pokazuje pełne etykiety).
       Samo okno („QuickAccessModal") zostaje zamontowane, ale po tej zmianie NIE MA JUŻ ŻADNEGO
       wejścia — świadomie i wprost, żeby nikt później nie szukał psującego się skrótu. Jeśli ma wrócić,
       wraca z własnym przyciskiem, a nie doklejone do lupy, która obiecuje szukanie. */
    const otworzSzukanie = useCallback(() => {
        if (isMobile) return;
        openWyszukiwarke();
    }, [isMobile, openWyszukiwarke]);

    useEffect(() => {
        const naKlawisz = (e) => {
            if (e.defaultPrevented) return;
            if (!(e.ctrlKey || e.metaKey) || e.key?.toLowerCase() !== "k") return;
            e.preventDefault();
            otworzSzukanie();
        };
        window.addEventListener("keydown", naKlawisz);
        return () => window.removeEventListener("keydown", naKlawisz);
    }, [otworzSzukanie]);

    // Cel z adresu, nigdy z historii przeglądarki (patrz nota przy `celPowrotu`).
    const handleBack = () => {
        if (celPowrotu) navigate(celPowrotu.cel);
    };

    const currentLang = languages.find((l) => l.gtCode === selectedLang);

    return (
        <>
            <header className={styles.header}>
                <div className={styles.leftSection}>
                    <button className={styles.hamburgerBtn} onClick={toggleMobileSidebar} title="Menu">
                        <FiMenu />
                    </button>

                    {/* title="Powrót" STAŁE (zapadki getByTitle); napis = nazwa celu („Obsługa IT”, „Panel administracyjny”, „Wykaz KSC”…);
                        na telefonie napis chowa arkusz (Header.module.css, icon-only), przycisk zostaje w drzewie.
                        aria-label zaczyna się od „Powrót”, bo napis celu może brzmieć „Obsługa IT” — a nazwa dostępna /^Obsługa/ jest
                        zarezerwowana dla przycisku obsługi (zapadki 1001 (0)(4)(5) szukają go po roli i nazwie; na telefonie ma go NIE być).
                        data-testid INNY niż przycisk powrotu dawnej kolejki (powrot-z-kolejki) — tamte zapadki pilnują jego BRAKU. */}
                    {showBackButton && (
                        <button
                            className={styles.backBtn}
                            onClick={handleBack}
                            title="Powrót"
                            aria-label={`Powrót: ${celPowrotu.etykieta}`}
                            data-testid="powrot-naglowka"
                        >
                            <FiArrowLeft size={15} />
                            <span>{celPowrotu.etykieta}</span>
                        </button>
                    )}

                    {/* [zgłoszenie właściciela 2026-09-16] Na telefonie widgetu wyszukiwarki NIE MA — patrz nota
                        przy `otworzSzukanie`. Warunek stoi tutaj, a nie w arkuszu: ukrycie przez CSS zostawiłoby
                        pole w drzewie, dalej osiągalne tabulatorem i dla czytnika ekranu. */}
                    {!isMobile && (
                        <div
                            className={styles.searchBar}
                            onClick={otworzSzukanie}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); otworzSzukanie(); } }}
                            role="button"
                            tabIndex={0}
                            title="Szukaj w aplikacji (Ctrl+K)"
                            aria-label="Szukaj w aplikacji"
                            data-testid="pasek-szukania"
                        >
                            <span className={styles.searchIcon}><FiSearch size={15} /></span>
                            <span className={styles.searchPlaceholder}>Szukaj...</span>
                            <kbd className={styles.searchKbd}>
                                <FiCommand size={10} />K
                            </kbd>
                        </div>
                    )}
                </div>

                <div className={styles.headerActions}>
                    {/* Action buttons group */}
                    <div className={styles.actionsGroup}>
                        {languages.length > 0 && (
                            <div className={styles.langSelector} ref={langRef}>
                                <button
                                    className={`${styles.actionBtn} ${isLangOpen ? styles.actionBtnActive : ""}`}
                                    onClick={() => setIsLangOpen(!isLangOpen)}
                                    title="Zmieniam język"
                                >
                                    {/* [G2] Flaga rysowana przez nas (SVG). Emoji flag państw Windows/Chrome
                                        NIE MA w foncie systemowym — pokazywał zamiast nich same litery. */}
                                    <FlagIcon
                                        code={currentLang?.gtCode || selectedLang}
                                        label={currentLang?.nativeName}
                                        fallback={currentLang?.displayCode}
                                        size={13}
                                        className={styles.langFlag}
                                    />
                                    <span className={styles.langCode}>{currentLang?.displayCode || selectedLang.toUpperCase()}</span>
                                    <FiChevronDown size={12} className={`${styles.miniChevron} ${isLangOpen ? styles.miniChevronOpen : ""}`} />
                                </button>
                                {isLangOpen && (
                                    <div className={`${styles.dropdown} ${styles.langDropdown}`}>
                                        <div className={styles.dropdownHeader}>
                                            <span className={styles.dropdownHeaderLabel}>Język interfejsu</span>
                                        </div>
                                        {langBlad && (
                                            <div className={styles.langError} role="alert">{langBlad}</div>
                                        )}
                                        {languages.map((lang) => (
                                            <button
                                                key={lang.id}
                                                className={`${styles.dropdownItem} ${selectedLang === lang.gtCode ? styles.dropdownItemActive : ""}`}
                                                onClick={() => handleLangSelect(lang.gtCode)}
                                            >
                                                <FlagIcon
                                                    code={lang.gtCode}
                                                    label={lang.nativeName}
                                                    fallback={lang.displayCode}
                                                    size={13}
                                                    className={styles.langFlag}
                                                />
                                                <span className={styles.dropdownLabel}>{lang.nativeName}</span>
                                                <span className={styles.dropdownMeta}>{lang.displayCode}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <button
                            className={styles.actionBtn}
                            onClick={toggleTheme}
                            title={theme === "light" ? "Tryb ciemny" : "Tryb jasny"}
                        >
                            <div className={`${styles.themeIcon} ${theme === "dark" ? styles.themeRotate : ""}`}>
                                {theme === "light" ? <FiMoon size={16} /> : <FiSun size={16} />}
                            </div>
                        </button>

                        {/* Accessibility dropdown */}
                        <div className={styles.a11ySelector} ref={a11yRef}>
                            <button
                                className={`${styles.actionBtn} ${(!isDefault || !persoIsDefault) ? styles.a11yActive : ""} ${isA11yOpen ? styles.actionBtnActive : ""}`}
                                onClick={() => setIsA11yOpen(!isA11yOpen)}
                                title="Dostępność i personalizacja"
                                aria-label="Dostępność i personalizacja"
                            >
                                <FiEye size={16} />
                            </button>
                            {isA11yOpen && (
                                <div className={`${styles.dropdown} ${styles.a11yDropdown}`}>
                                    <div className={styles.dropdownHeader}>
                                        <span className={styles.dropdownHeaderLabel}>Dostępność</span>
                                    </div>

                                    {/* Rozmiar tekstu */}
                                    <div className={styles.a11yRow}>
                                        <FiType size={15} className={styles.a11yRowIcon} />
                                        <span className={styles.a11yRowLabel}>Rozmiar tekstu</span>
                                        <div className={styles.a11yScaleControls}>
                                            <button
                                                className={styles.a11yScaleBtn}
                                                onClick={decreaseFontScale}
                                                disabled={!canDecrease}
                                                title="Zmniejsz czcionkę"
                                                aria-label="Zmniejsz rozmiar czcionki"
                                            >
                                                <FiMinus size={13} />
                                            </button>
                                            <span className={styles.a11yScaleValue}>{fontScaleLabel}</span>
                                            <button
                                                className={styles.a11yScaleBtn}
                                                onClick={increaseFontScale}
                                                disabled={!canIncrease}
                                                title="Powiększ czcionkę"
                                                aria-label="Powiększ rozmiar czcionki"
                                            >
                                                <FiPlus size={13} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Wysoki kontrast */}
                                    <div className={styles.a11yRow} onClick={toggleHighContrast} role="button" tabIndex={0}>
                                        <FiEye size={15} className={styles.a11yRowIcon} />
                                        <span className={styles.a11yRowLabel}>Wysoki kontrast</span>
                                        <span className={`${styles.a11yToggle} ${highContrast ? styles.a11yToggleOn : ""}`}>
                                            {highContrast ? "ON" : "OFF"}
                                        </span>
                                    </div>

                                    {/* Tryb czytania */}
                                    <div className={styles.a11yRow} onClick={toggleReaderMode} role="button" tabIndex={0}>
                                        <FiBookOpen size={15} className={styles.a11yRowIcon} />
                                        <span className={styles.a11yRowLabel}>Tryb czytania</span>
                                        <span className={`${styles.a11yToggle} ${readerMode ? styles.a11yToggleOn : ""}`}>
                                            {readerMode ? "ON" : "OFF"}
                                        </span>
                                    </div>

                                    {/* ── Personalizacja (zaawansowane) ── */}
                                    <div className={styles.dropdownDivider} />
                                    <div className={styles.a11ySectionLabel}>Personalizacja</div>

                                    {/* [2026-10-03] Motyw — jasny (domyślny) albo ciemny */}
                                    <div className={styles.persoRow}>
                                        {theme === "dark" ? <FiMoon size={15} className={styles.a11yRowIcon} /> : <FiSun size={15} className={styles.a11yRowIcon} />}
                                        <span className={styles.a11yRowLabel}>Motyw</span>
                                    </div>
                                    <div className={styles.motywWybor} role="group" aria-label="Motyw aplikacji" ref={grupaMotywuRef}>
                                        {MOTYWY_W_MENU.map(({ id, etykieta, Ikona }) => (
                                            <button
                                                key={id}
                                                type="button"
                                                className={`${styles.fontOption} ${styles.motywOpcja} ${theme === id ? styles.fontOptionActive : ""}`}
                                                onClick={() => wybierzMotyw(id)}
                                                aria-pressed={theme === id}
                                                aria-label={`Motyw: ${etykieta}`}
                                            >
                                                <Ikona size={13} aria-hidden="true" />
                                                <span>{etykieta}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {/* Kolor akcentu */}
                                    <div className={styles.persoRow}>
                                        <FiDroplet size={15} className={styles.a11yRowIcon} />
                                        <span className={styles.a11yRowLabel}>Kolor akcentu</span>
                                    </div>
                                    <div className={styles.swatchGrid}>
                                        {accents.map((a) => (
                                            <button
                                                key={a.id}
                                                type="button"
                                                className={`${styles.swatch} ${accent === a.id ? styles.swatchActive : ""}`}
                                                style={{ background: a.swatch }}
                                                onClick={() => setAccent(a.id)}
                                                title={a.label}
                                                aria-label={`Kolor akcentu: ${a.label}`}
                                                aria-pressed={accent === a.id}
                                            />
                                        ))}
                                    </div>

                                    {/* Czcionka */}
                                    <div className={styles.persoRow}>
                                        <FiType size={15} className={styles.a11yRowIcon} />
                                        <span className={styles.a11yRowLabel}>Czcionka</span>
                                    </div>
                                    <div className={styles.fontList}>
                                        {fonts.map((f) => (
                                            <button
                                                key={f.id}
                                                type="button"
                                                className={`${styles.fontOption} ${font === f.id ? styles.fontOptionActive : ""}`}
                                                style={{ fontFamily: f.preview }}
                                                onClick={() => setFont(f.id)}
                                                aria-pressed={font === f.id}
                                            >
                                                {f.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Przywróć domyślne (dostępność + personalizacja)
                                        [domknięcie po krytyku 2026-10-04] Od 3.10 przycisk zdejmuje też motyw — także z konta, czyli
                                        na wszystkich urządzeniach. BYŁO ŹLE: napis tego nie mówił, więc osoba w ciemnym wyglądzie,
                                        która chciała cofnąć tylko rozmiar tekstu, po cichu wracała do jasnego. Zdanie pod przyciskiem
                                        mówi wprost, co wraca. */}
                                    {(!isDefault || !persoIsDefault) && (
                                        <>
                                            <div className={styles.dropdownDivider} />
                                            <button
                                                type="button"
                                                className={styles.a11yResetBtn}
                                                onClick={() => { resetAll(); resetPersonalization(); }}
                                                aria-describedby="opis-przywrocenia-domyslnych"
                                            >
                                                <FiRotateCcw size={13} />
                                                <span>Przywróć domyślne</span>
                                            </button>
                                            <p id="opis-przywrocenia-domyslnych" className={styles.a11yResetOpis}>
                                                Wraca rozmiar tekstu, kontrast, tryb czytania, kolor, czcionka i jasny motyw.
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {czyCentrumPowiadomienWidoczne && <NotificationBell />}
                    </div>

                    {/* CTA Buttons */}
                    <div className={styles.ctaGroup}>
                        {/* [2026-07-27] Przyciski „Obsługa …" są WYŁĄCZNIE dynamiczne: pochodzą z modułów,
                            które ta osoba realnie obsługuje (dział przypisany do modułu w
                            /admin/access-control → „Moduły obsługi", nadanie imienne albo obsada desku).
                            Statyczny przycisk „Obsługa IT" pokazywany każdemu z kluczem TicketView został
                            usunięty — prowadził na landing modułu, którego użytkownik nie obsługuje. */}
                        {/* [polecenie właściciela 2026-09-06] JEDEN KAFELEK „Obsługa".
                            Jeden moduł — jeden przycisk prowadzący wprost. Więcej modułów — jeden przycisk
                            i lista do wyboru, żeby pasek nie rósł z każdym modułem dodanym w /admin/categories.
                            Zero modułów — nie ma przycisku: „jak nie mam dostępu, nie ma kompletnie tych
                            przycisków w menu; przyciski mapują się tylko wtedy, gdy mam fizyczny dostęp". */}
                        {moduleObslugi.length === 1 && (
                            <button
                                className={`${styles.ticketsBtn} ${moduleObslugi[0].aktywny && !modulKolejki ? styles.ticketsBtnActive : ""}`}
                                onClick={() => navigate(moduleObslugi[0].cel)}
                                style={moduleObslugi[0].kolor ? { borderColor: moduleObslugi[0].kolor } : undefined}
                                data-testid={modulKolejki ? "obsluga-kolejki" : undefined}
                                title={modulKolejki ? `Wróć do: ${moduleObslugi[0].etykieta}` : undefined}
                            >
                                <FiMessageSquare size={15} />
                                <span>{moduleObslugi[0].etykieta}</span>
                            </button>
                        )}

                        {moduleObslugi.length > 1 && (
                            <div className={styles.obslugaWrap} ref={obslugaRef}>
                                <button
                                    className={`${styles.ticketsBtn} ${moduleObslugi.some((m) => m.aktywny) && !modulKolejki ? styles.ticketsBtnActive : ""}`}
                                    onClick={() => setObslugaOtwarta((v) => !v)}
                                    aria-expanded={obslugaOtwarta}
                                    aria-haspopup="menu"
                                >
                                    <FiMessageSquare size={15} />
                                    <span>Obsługa</span>
                                    <span className={styles.obslugaLicznik}>{moduleObslugi.length}</span>
                                </button>
                                {obslugaOtwarta && createPortal(
                                    <div
                                        className={styles.obslugaLista}
                                        role="menu"
                                        ref={obslugaListaRef}
                                        data-testid="obsluga-lista"
                                        style={pozycjaListy ? { position: "fixed", top: pozycjaListy.top, right: pozycjaListy.right, zIndex: 1200 } : undefined}
                                    >
                                        {moduleObslugi.map((m) => (
                                            <button
                                                key={m.klucz}
                                                type="button"
                                                role="menuitem"
                                                className={`${styles.obslugaPozycja} ${m.aktywny ? styles.obslugaPozycjaAktywna : ""}`}
                                                /* Aktywny moduł oznaczony SEMANTYCZNIE, nie tylko klasą: czytnik ekranu
                                                   mówi wtedy „bieżąca strona”, a zapadka nie zależy od tego, czy arkusz
                                                   CSS Modules jest w testach zaślepiony. */
                                                aria-current={m.aktywny ? "page" : undefined}
                                                onClick={() => { setObslugaOtwarta(false); navigate(m.cel); }}
                                            >
                                                <span className={styles.obslugaKropka} style={m.kolor ? { background: m.kolor } : undefined} />
                                                {m.etykieta}
                                            </button>
                                        ))}
                                    </div>,
                                    document.body
                                )}
                            </div>
                        )}

                    </div>

                    <div className={styles.divider} />

                    {/* User profile */}
                    <div className={styles.userProfile} ref={menuRef}>
                        <button className={styles.userTrigger} onClick={() => setIsMenuOpen(!isMenuOpen)} type="button">
                            {/* [R40, właściciel 2026-09-13] Tu stał sam inicjał — nagłówek nigdy nie pokazywał zdjęcia.
                                Pudełko zostaje z arkusza nagłówka (także wersja < 768 px); bez zdjęcia ten sam inicjał. */}
                            <div className={styles.headerAvatar}>
                                <UserAvatar
                                    userId={user?.id}
                                    firstName={user?.firstName}
                                    lastName={user?.lastName}
                                    wypelnij
                                    dekoracyjny
                                    inicjaly={<span>{userInitial}</span>}
                                />
                            </div>
                            <div className={styles.userInfo}>
                                <span className={styles.userName}>{user ? `${user.firstName} ${user.lastName}` : "..."}</span>
                                <span className={styles.userRole}>{user?.department || getRoleName(user?.type)}</span>
                            </div>
                            <FiChevronDown size={13} className={`${styles.miniChevron} ${isMenuOpen ? styles.miniChevronOpen : ""}`} />
                        </button>

                        {isMenuOpen && (
                            <div className={`${styles.dropdown} ${styles.userDropdown}`}>
                                <div className={styles.userDropdownHeader}>
                                    <div className={styles.userDropdownAvatar}>
                                        <UserAvatar
                                            userId={user?.id}
                                            firstName={user?.firstName}
                                            lastName={user?.lastName}
                                            wypelnij
                                            dekoracyjny
                                            inicjaly={<span>{userInitial}</span>}
                                        />
                                    </div>
                                    <div className={styles.userDropdownMeta}>
                                        <span className={styles.userDropdownName}>{user ? `${user.firstName} ${user.lastName}` : ""}</span>
                                        <span className={styles.userDropdownRole}>{getRoleName(user?.type)}</span>
                                        <span className={styles.userDropdownEmail}>{user?.email || ""}</span>
                                    </div>
                                </div>
                                <div className={styles.dropdownDivider} />
                                <button className={styles.dropdownItem} onClick={() => { setIsMenuOpen(false); navigate(localizedPath("myTickets")); }}>
                                    <FiUser size={15} /><span className={styles.dropdownLabel}>Moje zgłoszenia</span>
                                </button>
                                {/* [zgłoszenie właściciela 2026-09-16] „Zablokuj wejście do sekcji admin z urządzeń
                                    mobilnych." Wejście znika RAZEM z blokadą trasy — samo zdjęcie pozycji z menu
                                    byłoby ozdobą (zakładka w przeglądarce dalej by weszła), a sama blokada trasy bez
                                    zdjęcia pozycji prowadziłaby prosto w ścianę. */}
                                {canAccessAdminPanel && !isMobile && (
                                    <button className={styles.dropdownItem} onClick={() => { setIsMenuOpen(false); navigate(localizedPath("admin")); }}>
                                        <FiSettings size={15} /><span className={styles.dropdownLabel}>Panel Admina</span>
                                    </button>
                                )}
                                {/* [2026-10-03] Stałe wejście do okna „Wygląd aplikacji” (to okno o nim mówi). */}
                                <button
                                    type="button"
                                    className={styles.dropdownItem}
                                    onClick={() => { setIsMenuOpen(false); window.dispatchEvent(new Event(ZDARZENIE_OTWORZ_OKNO_WYGLADU)); }}
                                >
                                    <FiDroplet size={15} /><span className={styles.dropdownLabel}>Wygląd aplikacji</span>
                                </button>
                                {maHaslo && (
                                    <button type="button" className={styles.dropdownItem} onClick={() => { setIsMenuOpen(false); setOknoHasla(true); }}>
                                        <FiKey size={15} /><span className={styles.dropdownLabel}>Zmień hasło</span>
                                    </button>
                                )}
                                <div className={styles.dropdownDivider} />
                                <button className={`${styles.dropdownItem} ${styles.dropdownDanger}`} onClick={logout}>
                                    <FiLogOut size={15} /><span className={styles.dropdownLabel}>Wyloguj się</span>
                                </button>
                                <button type="button" className={`${styles.dropdownItem} ${styles.dropdownDanger}`}
                                    onClick={() => { setIsMenuOpen(false); logout({ wszedzie: true }); }}
                                    title="Kończy sesję na każdym komputerze i telefonie, na którym jesteś zalogowany">
                                    <FiMonitor size={15} /><span className={styles.dropdownLabel}>Wyloguj ze wszystkich urządzeń</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <ChangelogModal isOpen={isChangelogOpen} onClose={() => setIsChangelogOpen(false)} changelog={changelog} loading={changelogLoading} />
            <ZmianaHaslaOkno isOpen={oknoHasla} onClose={() => setOknoHasla(false)} onZmieniono={setKomunikatHasla} />
            {komunikatHasla && (
                <button type="button" className={styles.komunikatHasla} role="status" onClick={() => setKomunikatHasla(null)}>{komunikatHasla}</button>
            )}

            <AnimatePresence>
                {langOverlay && (
                    <motion.div
                        className={styles.langOverlay}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                    >
                        <motion.div
                            className={styles.langOverlayCard}
                            initial={{ opacity: 0, scale: 0.92, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                        >
                            <div className={styles.langOverlaySpinner} />
                            <span className={styles.langOverlayTitle}>Zmieniamy j&#281;zyk interfejsu</span>
                            <span className={styles.langOverlaySubtitle}>Prze&#322;&#261;czamy na <strong>{langOverlay}</strong></span>
                            <span className={styles.langOverlayHint}>Bez prze&#322;adowania &#8212; zaraz gotowe...</span>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header;
